import { EMPTY_SYMBOL_24, ISDSIcon } from "@sznds/icons";
import { ISDSIconMulti } from "~/interfaces";
import { findFirstFocusableElement, fragment, node } from "~/util/dom";
import createSDSIcon from "~/ui/icon";
import { CHEVRON_DOWN, CHEVRON_UP } from "./sds-compatible-icons";
import ContextMenu from "~/ui/contextmenu";

interface IIconActionMenuButtonProps {
	icon: ISDSIcon | ISDSIconMulti;
	title: string;
	items: Array<any>;
	itemFactory: (item: any, menu: IconActionMenuButton) => HTMLElement;
	onOpen: (button: IconActionMenuButton) => void;
	onClose: (button: IconActionMenuButton) => void;
}

const DEFAULT_PROPS: IIconActionMenuButtonProps = {
	icon: EMPTY_SYMBOL_24,
	title: "",
	items: [],
	itemFactory: () => node("div"),
	onOpen: () => {},
	onClose: () => {},
};

export default class IconActionMenuButton {
	#container: HTMLButtonElement;

	#token: HTMLDivElement;

	#iconOn: HTMLMapyIconElement;

	#iconOff: HTMLMapyIconElement;

	#props: IIconActionMenuButtonProps;

	#isOpen: boolean;

	#lastActiveElement: Element | null;

	#menu: ContextMenu;

	constructor(props: Partial<IIconActionMenuButtonProps>) {
		const items = fragment();

		this.#props = {
			...DEFAULT_PROPS,
			...props,
		};
		this.#container = node("button", { type: "button", className: "icon-action-dropdown", title: this.#props.title });
		this.#token = node("div", { className: "icon-action-dropdown__token" });
		this.#iconOn = createSDSIcon({ symbol: CHEVRON_UP });
		this.#iconOff = createSDSIcon({ symbol: CHEVRON_DOWN });
		this.#token.append(node("span", {}, this.#props.title), this.#iconOff);
		this.#container.append(createSDSIcon({ symbol: this.#props.icon }), this.#token);
		this.#isOpen = false;
		this.#props.items.forEach(item => items.appendChild(this.#props.itemFactory(item, this)));
		this.#menu = new ContextMenu({
			anchor: this.#container,
			content: items,
			offset: 6,
			position: "bottom-end",
			onClose: () => {
				this.open(false);
			},
			focusTrap: true,
		});
		this.#container.addEventListener("click", this, false);
	}

	destroy() {
		this.#menu.destroy();
		this.#menu = null;
		this.#container.removeEventListener("click", this, false);
	}

	get container() {
		return this.#container;
	}

	open(isOpen: boolean, event?: PointerEvent) {
		if (isOpen !== this.#isOpen) {
			this.#isOpen = isOpen;

			if (this.#isOpen) {
				this.#iconOff.remove();
				this.#token.append(this.#iconOn);
				this.#open(event?.pointerType !== "mouse");
				this.#props.onOpen(this);
			} else {
				this.#iconOn.remove();
				this.#token.append(this.#iconOff);
				this.#close();
				this.#props.onClose(this);
			}
		}
	}

	handleEvent(event: PointerEvent) {
		if (event.type === "click") {
			this.open(!this.#isOpen, event);
		}
	}

	#open(focus: boolean) {
		this.#lastActiveElement = document.activeElement;
		this.#menu.open();
		focus && setTimeout(() => {
			findFirstFocusableElement(this.#menu.container)?.focus?.();
		}, 0);
	}

	#close() {
		this.#menu.close();
		(this.#lastActiveElement as HTMLElement)?.focus?.();
	}
}
